import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme, cssVar } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { useBudgetKindOptions } from 'hooks/useBudgetKindOptions';
import {
  emptyBudgetSearchCodes,
  useBudgetSearchCodeLookup,
} from 'hooks/useBudgetSearchCodeLookup';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { useGridPrintAction } from 'components/grid/print';
import AxisSliceBar from 'components/budget/AxisSliceBar';
import {
  ACCOUNTING_MASTER_CHANGED_EVENT,
  acctClassOptions,
  defaultAcctClassCode,
  acctClassLabel,
  acctUnitLabel,
  loadAcctClasses,
  loadAcctUnits,
} from 'budget/budgetAcctStore';
import { filterExpenseRequests, loadExpenseRequests } from 'budget/budgetExpenseRequestStore';
import { loadDepts } from 'master/deptStore';

const GRID_ID = 'budget-request-inquiry-2';
const AXIS_PREF_PREFIX = 'erp.axisSlice.budget-request-inquiry-2';
const EMPTY_KEY = '__empty__';

/** 페이지 축 화이트리스트 — 휠로 넘기는 기준은 1개만 */
export const INQUIRY_AXES = [
  { id: 'acctUnit', label: '회계단위', codeField: 'acctUnitCode', nameField: 'acctUnitNm' },
  { id: 'dept', label: '예산부서', codeField: 'deptCode', nameField: 'deptName' },
  { id: 'project', label: '세부사업', codeField: 'projectCode', nameField: 'projectName' },
];

const AXIS_BY_ID = Object.fromEntries(INQUIRY_AXES.map((a) => [a.id, a]));

const StackHost = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  margin-top: 4px;
  padding: 8px 12px 0 0;
  display: flex;
  flex-direction: column;
`;

const StackGhost = styled.div`
  position: absolute;
  top: 8px;
  right: 12px;
  bottom: 0;
  left: 0;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  pointer-events: none;
  transform: ${(p) => `translate(${p.$x}px, ${p.$y}px)`};
  opacity: ${(p) => p.$opacity};
`;

const StackFront = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

function axisPrefKey(prefsUserId) {
  return `${AXIS_PREF_PREFIX}|${prefsUserId || 'anon'}`;
}

function readAxisPref(prefsUserId) {
  try {
    const v = localStorage.getItem(axisPrefKey(prefsUserId));
    if (AXIS_BY_ID[v]) return v;
  } catch {
    /* ignore */
  }
  return 'dept';
}

function writeAxisPref(prefsUserId, axisId) {
  try {
    localStorage.setItem(axisPrefKey(prefsUserId), axisId);
  } catch {
    /* ignore */
  }
}

function memberLabel(code, name) {
  const c = String(code || '').trim();
  const n = String(name || '').trim();
  if (!c && !n) return '미지정';
  if (c && n && c !== n) return `${c} · ${n}`;
  return n || c;
}

function rowMemberKey(row, axis) {
  const code = String(row?.[axis.codeField] || '').trim();
  return code || EMPTY_KEY;
}

function buildMembers(rows, axisId) {
  const axis = AXIS_BY_ID[axisId];
  if (!axis) return [];
  const map = new Map();
  (rows || []).forEach((r) => {
    const key = rowMemberKey(r, axis);
    const code = String(r[axis.codeField] || '').trim();
    const name = String(r[axis.nameField] || '').trim();
    const cur = map.get(key);
    if (cur) {
      cur.count += 1;
      return;
    }
    map.set(key, { key, code, label: memberLabel(code, name), count: 1 });
  });
  return [...map.values()].sort((a, b) => {
    if (a.key === EMPTY_KEY) return 1;
    if (b.key === EMPTY_KEY) return -1;
    return String(a.code).localeCompare(String(b.code), 'ko');
  });
}

function filterByMember(rows, axisId, key) {
  const axis = AXIS_BY_ID[axisId];
  if (!axis || !key) return [];
  return (rows || []).filter((r) => rowMemberKey(r, axis) === key);
}

/**
 * 편성요구 마스터×산출내역을 한 행으로 펼친다 (마스터 1건당 디테일 N행)
 * - 디테일이 없는 마스터도 1행 유지 (산출 항목만 빈칸)
 */
function flattenRequestLines(lines = []) {
  const out = [];
  for (const m of lines) {
    if (m.mod === 'D') continue;
    const base = {
      requestUid: m.requestUid,
      acctUnitCode: m.acctUnitCode || '',
      acctUnitNm: m.acctUnitNm || '',
      deptCode: m.deptCode || '',
      deptName: m.deptName || '',
      projectCode: m.projectCode || '',
      projectName: m.projectName || '',
      subjectCode: m.subjectCode || '',
      subjectDispCode: m.subjectDispCode || m.subjectCode || '',
      subjectName: m.subjectName || '',
      fundName: m.fundName || '',
      budgetAmt: m.budgetAmt ?? 0,
      masterRemark: m.remark || '',
    };
    const details = (m.details || []).filter((d) => d && d.mod !== 'D');
    if (!details.length) {
      out.push({ ...base, id: m.requestUid, calcLevel: '', calcDesc: '', calcBasis: '', calcAmt: '' });
      continue;
    }
    details.forEach((d, i) => {
      out.push({
        ...base,
        id: `${m.requestUid}::${d.detailId || i}`,
        calcLevel: d.calcLevel ?? '',
        calcDesc: d.calcDesc || '',
        calcBasis: d.calcBasis || '',
        calcAmt: d.calcAmt ?? '',
        detailRemark: d.remark || '',
      });
    });
  }
  return out;
}

/** 라인에 회계단위가 없으면 부서 매핑값으로 보강 (시드·구데이터 호환) */
function attachAcctUnit(lines, deptByCode, tenantCode) {
  return (lines || []).map((m) => {
    const dept = deptByCode.get(String(m.deptCode || '').trim());
    const code = String(m.acctUnitCode || dept?.acctUnit || '').trim();
    const name =
      String(m.acctUnitNm || dept?.acctUnitNm || '').trim() ||
      (code ? acctUnitLabel(code, tenantCode) : '');
    return { ...m, acctUnitCode: code, acctUnitNm: name };
  });
}

/**
 * 편성내역조회2 — 동일 스키마 그리드 + 축(회계단위·예산부서·세부사업) 멤버 슬라이스
 */
const BudgetRequestInquiry2Inner = () => {
  const gridRef = useRef(null);
  const allRowsRef = useRef([]);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = getCurrentFiscalYear();

  const [acctMasterTick, setAcctMasterTick] = useState(0);
  const classOptions = useMemo(() => {
    void acctMasterTick;
    return acctClassOptions(tenantCode);
  }, [tenantCode, acctMasterTick]);
  const defaultAcctClass = useMemo(() => {
    void acctMasterTick;
    return defaultAcctClassCode(tenantCode);
  }, [tenantCode, acctMasterTick]);

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      fiscalYear: year,
      budgetKindCode: '',
      acctClass: defaultAcctClass,
      ...emptyBudgetSearchCodes(),
    }),
    [tenantCode, year, defaultAcctClass]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue, watch } = methods;
  const watchedFiscalYear = watch('fiscalYear');
  const { kindOptions, syncKindOptions } = useBudgetKindOptions({
    tenantCode,
    fiscalYear: watchedFiscalYear || year,
    getValues,
    setValue,
    autoSelect: true,
  });
  const getDims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      acctClass: v.acctClass || defaultAcctClass,
    };
  }, [getValues, tenantCode, year, defaultAcctClass]);
  const { lookupDialog, searchCodeFields } = useBudgetSearchCodeLookup({
    getDims,
    setValue,
    prefsUserId,
    sizeKeyPrefix: 'codeLookup-inquiry-2',
  });
  const [axisId, setAxisId] = useState(() => readAxisPref(prefsUserId));
  const [members, setMembers] = useState([]);
  const [memberKey, setMemberKey] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    setAxisId(readAxisPref(prefsUserId));
  }, [prefsUserId]);

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([
          loadAcctClasses(tenantCode),
          loadAcctUnits(tenantCode),
          loadDepts(tenantCode),
        ]);
      } catch {
        /* 마스터 미적재 시에도 조회는 시도 */
      }
      if (!cancelled) setAcctMasterTick((n) => n + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  useEffect(() => {
    const refresh = (e) => {
      if (e.detail?.tenantCode === tenantCode) {
        setAcctMasterTick((n) => n + 1);
      }
    };
    window.addEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
  }, [tenantCode]);

  const columns = useMemo(() => {
    const mergeMaster = { mergeRows: true, mergeGroupKey: 'requestUid' };
    return [
      hiCol.roTextColumn({ header: '회계단위코드', field: 'acctUnitCode', size: 100, ...mergeMaster }),
      hiCol.roTextColumn({ header: '회계단위명', field: 'acctUnitNm', size: 110, ...mergeMaster }),
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100, ...mergeMaster }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 130, ...mergeMaster }),
      hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 100, ...mergeMaster }),
      hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 200, ...mergeMaster }),
      hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 120, ...mergeMaster }),
      hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 180, ...mergeMaster }),
      hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 90, ...mergeMaster }),
      hiCol.roNumberColumn({
        header: '예산액(천원)',
        field: 'budgetAmt',
        size: 120,
        title: '마스터 예산액 — 동일 편성요구의 산출내역 행에서 병합 표시',
        ...mergeMaster,
      }),
      hiCol.roTextColumn({ header: '산출레벨', field: 'calcLevel', size: 80 }),
      hiCol.roTextColumn({ header: '산출내역', field: 'calcDesc', size: 220 }),
      hiCol.roTextColumn({ header: '산출기초', field: 'calcBasis', size: 200 }),
      hiCol.roNumberColumn({ header: '산출금액(천원)', field: 'calcAmt', size: 130 }),
      hiCol.roTextColumn({ header: '비고', field: 'masterRemark', size: 200, ...mergeMaster }),
    ];
  }, []);

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
  });

  const applySlice = useCallback((rows, nextAxisId, nextKey) => {
    const filtered = filterByMember(rows, nextAxisId, nextKey);
    gridRef.current?.setData?.(filtered);
    return filtered.length;
  }, []);

  const handleSearch = useCallback(async () => {
    const opts = await syncKindOptions();
    const v = getValues();
    const dims = {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      acctClass: v.acctClass || defaultAcctClass,
    };
    if (!dims.budgetKindCode) {
      showAlert(
        opts.length
          ? '예산차수(결합코드)를 선택하세요.'
          : '예산차수가 없습니다. 예산차수등록에서 해당 연도의 차수를 등록하세요.',
        'warning'
      );
      return;
    }
    try {
      const depts = await loadDepts(dims.tenantCode);
      const deptByCode = new Map(
        (depts || []).map((d) => [String(d.deptCode || '').trim(), d])
      );
      const lines = attachAcctUnit(await loadExpenseRequests(dims), deptByCode, dims.tenantCode);
      const rows = filterExpenseRequests(flattenRequestLines(lines), {
        deptCode: v.deptCode,
        projectCode: v.projectCode,
        subjectCode: v.subjectCode,
      });
      allRowsRef.current = rows;
      const nextMembers = buildMembers(rows, axisId);
      const keep = nextMembers.some((m) => m.key === memberKey) ? memberKey : nextMembers[0]?.key || '';
      setMembers(nextMembers);
      setMemberKey(keep);
      setSearched(true);
      const shown = applySlice(rows, axisId, keep);
      const kind = `${dims.budgetKindCode} · ${acctClassLabel(dims.acctClass, dims.tenantCode)}`;
      const axisNm = AXIS_BY_ID[axisId]?.label || axisId;
      const memNm = nextMembers.find((m) => m.key === keep)?.label || '없음';
      showAlert(
        `조회했습니다. [${kind}] ${axisNm} · ${memNm} ${shown}건 (전체 ${rows.length}건)`,
        'success'
      );
    } catch (e) {
      showAlert(e?.message || '조회 중 오류가 발생했습니다.', 'error');
    }
  }, [getValues, tenantCode, year, defaultAcctClass, axisId, memberKey, applySlice, syncKindOptions]);

  const handleAxisChange = useCallback(
    (nextId) => {
      if (!AXIS_BY_ID[nextId] || nextId === axisId) return;
      setAxisId(nextId);
      writeAxisPref(prefsUserId, nextId);
      if (!searched) return;
      const rows = allRowsRef.current;
      const nextMembers = buildMembers(rows, nextId);
      const nextKey = nextMembers[0]?.key || '';
      setMembers(nextMembers);
      setMemberKey(nextKey);
      applySlice(rows, nextId, nextKey);
    },
    [axisId, prefsUserId, searched, applySlice]
  );

  const handleMemberChange = useCallback(
    (nextKey) => {
      if (!nextKey || nextKey === memberKey) return;
      setMemberKey(nextKey);
      if (!searched) return;
      applySlice(allRowsRef.current, axisId, nextKey);
    },
    [memberKey, searched, axisId, applySlice]
  );

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '편성내역',
    getData,
    getDataColumns,
    joinKeys: ['requestUid'],
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '편성내역조회2' });
  const { printButton, dialog: printDialog } = useGridPrintAction({
    fileName: '편성내역조회2',
    prefsUserId,
    defaultTitle: '편성내역조회',
  });

  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    printButton,
    excelButton,
  ];

  const searchFields = [
    [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w100,
        labelWidth: SEARCH_LABEL_W,
        required: true,
      },
      {
        name: 'budgetKindCode',
        label: '예산차수',
        type: 'select',
        width: CommonCss.w140,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '선택' }, ...kindOptions],
        required: true,
      },
      {
        name: 'acctClass',
        label: '회계구분',
        type: 'select',
        width: CommonCss.w110,
        labelWidth: SEARCH_LABEL_W,
        options: classOptions,
        required: true,
      },
    ],
    searchCodeFields,
  ];

  const showStack = members.length > 1;

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel
          storageKey="budget-request-inquiry-2"
          prefsUserId={prefsUserId}
          summary={`회계연도 ${watchedFiscalYear || year}`}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>
      <AxisSliceBar
        axes={INQUIRY_AXES}
        axisId={axisId}
        onAxisChange={handleAxisChange}
        members={members}
        memberKey={memberKey}
        onMemberChange={handleMemberChange}
        disabled={!searched}
        hint="조회조건은 유지 · 휠은 그리드가 아닌 이 영역"
      />
      <GridExportFocusScope gridId={GRID_ID}>
        <StackHost>
          {showStack ? <StackGhost $x={10} $y={-8} $opacity={0.28} /> : null}
          {showStack ? <StackGhost $x={5} $y={-4} $opacity={0.5} /> : null}
          <StackFront>
            <HiGridComponent
              ref={gridRef}
              columns={columns}
              height="100%"
              showRowNumber
              rowNumberSize={56}
              enableColumnFilters
              enableContextMenu
              enableRowEditMenu={false}
              showModColumn={false}
              enableRowCheck={false}
              enableCut={false}
              enableFindReplace
              allowReplace={false}
              enableHeaderMenu
              enableSelectionStats
              enableFreeze={false}
              enableQuickExport
              exportFileName="편성내역조회2"
              gridId={GRID_ID}
              prefsUserId={prefsUserId}
            />
          </StackFront>
        </StackHost>
      </GridExportFocusScope>
      {excelDialog}
      {printDialog}
      {lookupDialog}
    </PageContainer>
  );
};

const BudgetRequestInquiry2 = () => (
  <GridExportProvider>
    <BudgetRequestInquiry2Inner />
  </GridExportProvider>
);

export default BudgetRequestInquiry2;
